import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { MFA_STEP_UP_REQUIRED_MESSAGE } from '../../constants/mfaStepUp';
import { enqueueMfaStepUpFailure } from '../mfaStepUpQueue';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { REDIRECT_QUERY_PARAM } from '../../utils/redirectAfterLogin';
import { getToken, isTokenExpired, removeToken } from '../../utils/security/tokenStorage';

const _ = require('dotenv').config();

const API_ADDRESS = process.env.REACT_APP_API_ADDRESS
  ? process.env.REACT_APP_API_ADDRESS
  : 'https://wireless-api.herokuapp.com';

const apiClient = axios.create({
  baseURL: API_ADDRESS,
});

// Endpoints we allow without *client-side* token-expiry blocking.
// Note: backend may still require authentication; we still attach the token
// when it exists.
const PUBLIC_ENDPOINTS = [
  'api/auth/login',
  'api/auth/validate-azure',
  'api/auth/validate-google',
  'api/auth/mfa/verify',
  'api/auth/mfa/dispatch',
  'api/auth/logout',
  'api/auth/reset-password',
  'api/auth/forgot-password',
  'api/auth/request-unlock',
];

/**
 * Check if the request URL is allowlisted to skip local token-expiry blocking.
 * Uses precise matching to avoid false positives
 */
const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  
  return PUBLIC_ENDPOINTS.some(endpoint => {
    // Exact match
    if (url === endpoint) return true;
    
    // Match if URL starts with endpoint followed by /, ?, or end of string
    // This prevents matching "api/auth/login-attempt" with "api/auth/login"
    const pattern = new RegExp(`^${endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[/?#]|$)`);
    return pattern.test(url);
  });
};

// Prevent multiple simultaneous redirects
let isRedirecting = false;

/**
 * Redirect to login page if not already there
 * Includes guard to prevent multiple simultaneous redirects
 */
const redirectToLogin = (): void => {
  // Guard against multiple simultaneous redirects
  if (isRedirecting) {
    return;
  }
  
  // SSR guard - check if running in browser
  if (typeof window === 'undefined') {
    return;
  }
  
  const pathname = window.location.pathname;
  // Public auth and legal surfaces — do not bounce these through /auth?from=...
  const isLoginPage =
    pathname === '/auth' ||
    pathname === '/login' ||
    pathname === '/' ||
    pathname === '/auth/google/callback' ||
    pathname === APP_ROUTES.TERMS_OF_SERVICE ||
    pathname === APP_ROUTES.PRIVACY_POLICY;

  if (!isLoginPage) {
    isRedirecting = true;
    const fullPath = pathname + window.location.search + (window.location.hash || '');
    window.location.href = `/auth?${REDIRECT_QUERY_PARAM}=${encodeURIComponent(fullPath)}`;
  }
};

// Request interceptor: Add token and check expiration
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url || '';
    const isPublic = isPublicEndpoint(url);

    // Skip local token-expiry checks for allowlisted endpoints (login, logout, etc.)
    if (!isPublic) {
      // Check if token is expired before making request
      if (isTokenExpired()) {
        removeToken();
        // Redirect to login immediately since request won't reach server
        redirectToLogin();
        return Promise.reject(new Error('Token expired'));
      }
    }

    // Attach token when present. We only skip the *local* expiry guard for allowlisted endpoints.
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors globally
apiClient.interceptors.response.use(
  response => response,
  (error: unknown) => {
    // Defensive error type checking
    if (!error || typeof error !== 'object') {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized from server - token invalid or expired
    if (error instanceof AxiosError && error.response?.status === 401) {
      const requestUrl = error.config?.url ?? '';
      const bodyMessage = error.response?.data?.message;
      const isStepUpRequired =
        typeof bodyMessage === 'string' && bodyMessage === MFA_STEP_UP_REQUIRED_MESSAGE;
      if (isStepUpRequired) {
        return enqueueMfaStepUpFailure(error);
      }
      const isPrimaryAuthRequest =
        requestUrl === 'api/auth/login' ||
        requestUrl.endsWith('/api/auth/login') ||
        requestUrl === 'api/auth/validate-azure' ||
        requestUrl.endsWith('/api/auth/validate-azure') ||
        requestUrl === 'api/auth/validate-google' ||
        requestUrl.endsWith('/api/auth/validate-google');
      const isLogoutRequest = requestUrl === 'api/auth/logout' || requestUrl.endsWith('/api/auth/logout');
      // Wrong MFA verify code, enrollment failures, etc. — keep session / enrollment token; show inline error.
      // `mfa/remove` / `mfa/status`: 401 means the session is not valid; clear token like other API calls.
      const isMfaAuthFlowRequest =
        (requestUrl.startsWith('api/auth/mfa/') || requestUrl.includes('/api/auth/mfa/')) &&
        !requestUrl.includes('mfa/remove') &&
        !requestUrl.includes('mfa/status');
      // Don't redirect or clear token when 401 is from primary auth (locked account / not provisioned / etc.)
      if (!isPrimaryAuthRequest && !isLogoutRequest && !isMfaAuthFlowRequest) {
        removeToken();
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
